/**
 * Firestore Fetch Policy Utilities
 *
 * Centralises the environment-variable-based fetch policy detection that was
 * previously copy-pasted into every Firestore read helper:
 *   - getPostComments       (userOperations.js)
 *   - getCommentReplies     (userOperations.js)
 *   - fetchPostComments     (commentSlice.js)
 *   - any future collection readers
 *
 * Supported policy values:
 *   'cache-first'  — try Firestore local cache first, fall back to server  (default)
 *   'network-only' — always read from the server, bypass cache
 *   'cache-only'   — only read from local cache, never hit the server
 */

/**
 * Resolves the active Firestore fetch policy.
 *
 * Priority order (highest → lowest):
 *   1. `override` argument — an explicit policy passed by the caller
 *      (e.g. the `fetchPolicy` param on getPostComments)
 *   2. REACT_APP_FORCE_SERVER_COMMENT_READS=true  → 'network-only'
 *   3. REACT_APP_COMMENT_FETCH_POLICY env var     → its value
 *   4. Default: 'cache-first'
 *
 * @param {string|null|undefined} override
 *   Explicit policy string supplied by the call site. Pass `null` or `undefined`
 *   to fall through to the environment-variable resolution.
 *
 * @returns {'cache-first'|'network-only'|'cache-only'}
 */
export function getFirestoreFetchPolicy(override = null) {
  if (override) return override;

  if (process.env.REACT_APP_FORCE_SERVER_COMMENT_READS === 'true') {
    return 'network-only';
  }

  return process.env.REACT_APP_COMMENT_FETCH_POLICY || 'cache-first';
}

/**
 * Returns true when the app is running in low-quota mode.
 *
 * In low-quota mode, non-critical Firestore reads should be suppressed to
 * avoid burning through the free-tier quota.  Callers should skip server
 * fallbacks and return whatever is in the local cache (even if empty).
 *
 * Controlled by the REACT_APP_LOW_QUOTA_MODE=true environment variable.
 *
 * @returns {boolean}
 */
export function isLowQuotaMode() {
  return process.env.REACT_APP_LOW_QUOTA_MODE === 'true';
}
